/**
 * Bridge between Profile types (saved bow/arrow gear) and Setup types
 * (v137 multi-arrow Builder/Wizard state).
 *
 * Why this exists: in v140 we landed two parallel gear stores —
 *   - BowProfile + ArrowProfile (src/profiles/) — used by Wind-Drift visualizer
 *   - Setup (src/hooks/useSetups.ts) — used by Builder + Tune Wizard
 *
 * When a user saves a bow on /bows.html and then opens Tune, the Wizard
 * was forcing them to re-enter the same bow info because Setup.bow is a
 * separate field. This bridge converts a BowProfile → BowSetup so the
 * Setup auto-populates from the user's primary bow profile.
 *
 * Two integration points:
 *   1. useSetups bootstrap — if no Setup exists AND a primary BowProfile
 *      exists, the first Setup is created with bow seeded from the profile.
 *   2. PreflightStep "Load from My Bows" button — pulls the primary
 *      BowProfile into the active Setup's bow field on demand.
 */

import type { BowSetup, SightSelection, BowType } from '@/types';
import type { BowProfile } from './types';
import { BOWS } from '@/data/bows';
import { getPrimaryBow } from './storage';

/**
 * Convert a BowProfile to a BowSetup. Best-effort match against the
 * BOWS catalog to populate category + year so PreflightStep's spec lookup
 * works out of the box.
 */
export function bowSetupFromProfile(profile: BowProfile): BowSetup {
  const brand = profile.bow.brand;
  const model = profile.bow.model;

  /* Match against catalog to pull category + year. We pick the newest
   * year matching brand + model, since the BowProfile doesn't store year. */
  const catalogMatches = BOWS.filter(
    (b) => brand && model && b.brand === brand && b.model === model
  ).sort((a, b) => b.year - a.year);
  const catalogMatch = catalogMatches[0];

  /* Determine bow type from catalog or default to compound */
  const type: BowType = inferBowType(catalogMatch);

  return {
    type,
    drawWeight: profile.bow.drawWeightLb,
    drawLength: profile.bow.drawLengthIn,
    iboSpeed: profile.bow.iboFps,
    letOff: catalogMatch?.letOff,
    category: catalogMatch ? (isBowCategoryTarget(catalogMatch) ? 'target' : 'hunting') : undefined,
    brand,
    model,
    year: catalogMatch?.year,
  };
}

/**
 * Convert a BowProfile's sight spec to a SightSelection (catalog index +
 * pin diameter). We can't always match housing → SIGHTS catalog index, so
 * the housing/scope strings stay on the profile and the Setup gets a
 * sensible default sight with the user's pin diameter.
 */
export function sightSelectionFromProfile(profile: BowProfile): SightSelection {
  return {
    sightIdx: 0,
    pinDiameter: profile.sight.pinDiameterIn ?? 0.019,
  };
}

function inferBowType(catalogMatch: (typeof BOWS)[number] | undefined): BowType {
  if (!catalogMatch) return 'compound';
  /* BOWS catalog entries have category like 'hunting' / 'target' / 'recurve' / 'traditional'.
   * For now everything that isn't recurve/traditional is compound. */
  const cat = (catalogMatch as { category?: string }).category ?? '';
  if (cat.includes('recurve')) return 'recurve';
  if (cat.includes('traditional') || cat.includes('trad')) return 'traditional';
  return 'compound';
}

function isBowCategoryTarget(catalogMatch: (typeof BOWS)[number]): boolean {
  /* Use bowCategory helper convention from data/bows: hunting vs target */
  const ata = (catalogMatch as { axleToAxle?: number }).axleToAxle ?? 32;
  return ata >= 34;
}

/**
 * Convenience: get a BowSetup from whatever the user's primary BowProfile
 * is. Returns null if no profiles saved.
 */
export function primaryBowSetup(): { bow: BowSetup; sight: SightSelection; profile: BowProfile } | null {
  const profile = getPrimaryBow();
  if (!profile) return null;
  return {
    bow: bowSetupFromProfile(profile),
    sight: sightSelectionFromProfile(profile),
    profile,
  };
}
